import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/**
 * Lightweight chip/tag input.
 * - Value is a list of normalized lowercase strings.
 * - Enter or comma commits a tag. Backspace on empty input removes the last tag.
 * - Optional `suggestions` autocomplete (filtered by current input).
 *
 * Used for caregiver skills / languages and client required/preferred skills.
 */
export default function TagInput({
  value = [],
  onChange,
  suggestions = [],
  placeholder = "Type and press Enter…",
  testid,
  tagTone = "default", // "default" | "required" | "preferred"
}) {
  const [draft, setDraft] = useState("");
  const [showSug, setShowSug] = useState(false);
  const inputRef = useRef(null);

  const normalized = (s) => (s || "").trim().toLowerCase();

  const commit = (raw) => {
    const t = normalized(raw);
    if (!t) return;
    if (value.includes(t)) {
      setDraft("");
      return;
    }
    onChange?.([...value, t]);
    setDraft("");
  };

  const removeAt = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange?.(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  const matchedSug = suggestions
    .filter((s) => normalized(s).includes(normalized(draft)) && !value.includes(normalized(s)))
    .slice(0, 6);

  // Close suggestions when clicking outside
  useEffect(() => {
    if (!showSug) return;
    const handler = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSug]);

  const toneCls = {
    required: "bg-rose-50 text-rose-700 border-rose-200",
    preferred: "bg-amber-50 text-amber-700 border-amber-200",
    default: "bg-clinical-bg text-clinical-text border-clinical-border",
  }[tagTone];

  return (
    <div className="relative" ref={inputRef} data-testid={testid}>
      <div className="min-h-[40px] flex flex-wrap items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-2 py-1.5 focus-within:border-clinical-primary focus-within:ring-2 focus-within:ring-clinical-primary/15">
        {value.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${toneCls}`}
            data-testid={testid ? `${testid}-chip-${t}` : undefined}
          >
            {t}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="hover:opacity-70"
              aria-label={`Remove ${t}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setShowSug(true); }}
          onKeyDown={onKey}
          onFocus={() => setShowSug(true)}
          onBlur={() => commit(draft)}
          placeholder={value.length ? "" : placeholder}
          className="flex-1 min-w-[140px] outline-none bg-transparent text-sm py-0.5"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </div>
      {showSug && draft && matchedSug.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 rounded-lg border border-clinical-border bg-white shadow-md overflow-hidden">
          {matchedSug.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(s); setShowSug(false); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-clinical-bg"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
